/**
 * ChatPreferences — the settings that follow you across every conversation.
 *
 * Rendered in two places from this one component: the gear in the chat list
 * header, and the "Chat" tab in account settings.
 */

import { useState } from "react";
import { Check, Palette, RotateCcw, Sparkles, Users, Volume2 } from "lucide-react";
import { cn } from "@slumbr/ui";

import { getCurrentUserId, getCurrentUserName } from "../stores/messenger-store";
import { useChatSettingsStore } from "../stores/chat-settings-store";
import { senderColor, initialsOf } from "./sender-identity";
import { nameStyle } from "./name-style";
import type { NameEffect } from "../types";

/** Twelve tones that stay legible on the dark chat canvas. */
const COLOR_CHOICES = [
  "#ff8fac",
  "#ff9e7a",
  "#f7b955",
  "#d8cf5a",
  "#8fd67f",
  "#5ad2b8",
  "#63c9e8",
  "#8aa8ff",
  "#a68cff",
  "#d18cff",
  "#ff8fd6",
  "#e0b48a",
];

const HEX_PATTERN = /^#[0-9a-fA-F]{6}$/;

export function ChatPreferences({ compact = false }: { compact?: boolean }) {
  const myChatColor = useChatSettingsStore((s) => s.myChatColor);
  const setMyChatColor = useChatSettingsStore((s) => s.setMyChatColor);
  const soundEnabled = useChatSettingsStore((s) => s.soundEnabled);
  const setSoundEnabled = useChatSettingsStore((s) => s.setSoundEnabled);
  const myChatColorAlt = useChatSettingsStore((s) => s.myChatColorAlt);
  const myNameEffect = useChatSettingsStore((s) => s.myNameEffect);
  const setMyNameStyle = useChatSettingsStore((s) => s.setMyNameStyle);
  const colorfulNames = useChatSettingsStore((s) => s.colorfulNames);
  const setColorfulNames = useChatSettingsStore((s) => s.setColorfulNames);
  const reset = useChatSettingsStore((s) => s.reset);

  const myId = getCurrentUserId();
  const myName = getCurrentUserName();
  const fallback = senderColor(myId);
  const active = myChatColor ?? fallback;
  const [customHex, setCustomHex] = useState(myChatColor ?? "");
  const usesGradient = myNameEffect === "gradient" || myNameEffect === "gradient-glow";
  const glows = myNameEffect === "glow" || myNameEffect === "gradient-glow";
  const secondary = myChatColorAlt ?? "#8aa8ff";
  const previewCss = nameStyle({
    color: active,
    colorAlt: myChatColorAlt ?? undefined,
    effect: myNameEffect,
  });

  /** Flip one axis of the effect without clobbering the other. */
  const setEffect = (nextGradient: boolean, nextGlow: boolean) => {
    const effect: NameEffect =
      nextGradient && nextGlow
        ? "gradient-glow"
        : nextGradient
          ? "gradient"
          : nextGlow
            ? "glow"
            : "none";
    setMyNameStyle({
      effect,
      // Gradients need a second stop; seed one the first time it's turned on.
      ...(nextGradient && !myChatColorAlt ? { colorAlt: secondary } : {}),
    });
  };

  return (
    <div className={cn("flex flex-col", compact ? "gap-5" : "gap-6")}>
      <section>
        <SectionLabel icon={<Palette className="h-3 w-3 text-[#ff6b8f]" />}>
          Your colour
        </SectionLabel>
        <p className="mb-3 text-[12px] leading-relaxed text-[#71767b]">
          Marks your name and your avatar everywhere you show up in chat.
        </p>

        {/* Live sample — the exact treatment used in a group conversation. */}
        <div className="mb-3 flex items-center gap-2.5 rounded-2xl bg-white/[0.04] px-3 py-2.5 ring-1 ring-inset ring-white/[0.06]">
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/[0.07] text-[12px] font-bold"
            style={{ color: active, boxShadow: `inset 0 0 0 1.5px ${active}59` }}
          >
            {initialsOf(myName)}
          </span>
          <span className="min-w-0">
            <span
              className="block truncate text-[14px] font-semibold leading-5"
              style={previewCss}
            >
              {myName}
            </span>
            <span className="block text-[12px] leading-4 text-[#9aa4b0]">
              This is how you'll read in a group.
            </span>
          </span>
        </div>

        <div className="grid grid-cols-6 gap-2">
          {COLOR_CHOICES.map((hex) => {
            const selected = myChatColor === hex;
            return (
              <button
                key={hex}
                type="button"
                onClick={() => {
                  setMyChatColor(hex);
                  setCustomHex(hex);
                }}
                aria-label={`Use ${hex}`}
                className={cn(
                  "flex h-9 items-center justify-center rounded-xl transition",
                  selected ? "scale-105" : "hover:scale-105"
                )}
                style={{
                  backgroundColor: `${hex}26`,
                  boxShadow: selected
                    ? `inset 0 0 0 2px ${hex}`
                    : `inset 0 0 0 1px ${hex}55`,
                }}
              >
                {selected ? (
                  <Check className="h-4 w-4" style={{ color: hex }} />
                ) : (
                  <span
                    className="h-3.5 w-3.5 rounded-full"
                    style={{ backgroundColor: hex }}
                  />
                )}
              </button>
            );
          })}
        </div>

        <div className="mt-3 flex items-center gap-2">
          <span className="text-[10px] font-black uppercase tracking-[0.18em] text-[#536471]">
            Custom
          </span>
          <input
            type="color"
            value={active}
            onChange={(e) => {
              setMyChatColor(e.target.value);
              setCustomHex(e.target.value);
            }}
            aria-label="Pick a custom colour"
            className="h-7 w-10 cursor-pointer rounded border border-white/[0.08] bg-transparent"
          />
          <input
            type="text"
            value={customHex}
            onChange={(e) => setCustomHex(e.target.value)}
            onBlur={() => {
              if (HEX_PATTERN.test(customHex)) setMyChatColor(customHex);
            }}
            placeholder={fallback}
            className="min-w-0 flex-1 rounded-lg bg-black/30 px-3 py-1.5 text-sm text-[#e7e9ea] outline-none ring-1 ring-inset ring-white/[0.06] transition focus:ring-[#ff6b8f]/40"
          />
          {myChatColor && (
            <button
              type="button"
              onClick={() => {
                setMyChatColor(null);
                setCustomHex("");
              }}
              className="shrink-0 rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wide text-[#71767b] transition hover:bg-white/[0.06] hover:text-[#e7e9ea]"
            >
              Auto
            </button>
          )}
        </div>
      </section>

      <section>
        <SectionLabel icon={<Sparkles className="h-3 w-3 text-[#ff6b8f]" />}>
          Name style
        </SectionLabel>
        <p className="mb-3 text-[12px] leading-relaxed text-[#71767b]">
          Fade your name between two colours, give it a glow, or both.
        </p>

        <div className="flex flex-col gap-1">
          <ToggleRow
            icon={<Palette className="h-4 w-4" />}
            label="Gradient"
            hint="Blend your colour into a second one."
            checked={usesGradient}
            onChange={(on) => setEffect(on, glows)}
          />
          <ToggleRow
            icon={<Sparkles className="h-4 w-4" />}
            label="Glow"
            hint="Add a soft halo around your name."
            checked={glows}
            onChange={(on) => setEffect(usesGradient, on)}
          />
        </div>

        {usesGradient && (
          <div className="mt-3">
            <p className="mb-2 text-[10px] font-black uppercase tracking-[0.18em] text-[#536471]">
              Second colour
            </p>
            <div className="grid grid-cols-6 gap-2">
              {COLOR_CHOICES.map((hex) => {
                const selected = secondary === hex;
                return (
                  <button
                    key={`alt-${hex}`}
                    type="button"
                    onClick={() => setMyNameStyle({ colorAlt: hex })}
                    aria-label={`Fade into ${hex}`}
                    className={cn(
                      "flex h-9 items-center justify-center rounded-xl transition",
                      selected ? "scale-105" : "hover:scale-105"
                    )}
                    style={{
                      backgroundColor: `${hex}26`,
                      boxShadow: selected
                        ? `inset 0 0 0 2px ${hex}`
                        : `inset 0 0 0 1px ${hex}55`,
                    }}
                  >
                    {selected ? (
                      <Check className="h-4 w-4" style={{ color: hex }} />
                    ) : (
                      <span
                        className="h-3.5 w-3.5 rounded-full"
                        style={{ backgroundColor: hex }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <span className="text-[10px] font-black uppercase tracking-[0.18em] text-[#536471]">
                Custom
              </span>
              <input
                type="color"
                value={secondary}
                onChange={(e) => setMyNameStyle({ colorAlt: e.target.value })}
                aria-label="Pick the second colour"
                className="h-7 w-10 cursor-pointer rounded border border-white/[0.08] bg-transparent"
              />
            </div>
          </div>
        )}
      </section>

      <section>
        <SectionLabel icon={<Volume2 className="h-3 w-3 text-[#ff6b8f]" />}>
          Behaviour
        </SectionLabel>
        <div className="flex flex-col gap-1">
          <ToggleRow
            icon={<Volume2 className="h-4 w-4" />}
            label="Message sounds"
            hint="Play a ping when a message arrives."
            checked={soundEnabled}
            onChange={setSoundEnabled}
          />
          <ToggleRow
            icon={<Users className="h-4 w-4" />}
            label="Colour everyone's names"
            hint="Give each person their own tone in group chats."
            checked={colorfulNames}
            onChange={setColorfulNames}
          />
        </div>
      </section>

      <button
        type="button"
        onClick={() => {
          reset();
          setCustomHex("");
        }}
        className="inline-flex items-center gap-1.5 self-start rounded-full px-3 py-1.5 text-[11px] font-black uppercase tracking-wide text-[#71767b] transition hover:bg-white/[0.05] hover:text-[#e7e9ea]"
      >
        <RotateCcw className="h-3 w-3" />
        Reset chat settings
      </button>
    </div>
  );
}

function SectionLabel({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <p className="mb-2 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-[#536471]">
      {icon}
      {children}
    </p>
  );
}

function ToggleRow({
  icon,
  label,
  hint,
  checked,
  onChange,
}: {
  icon: React.ReactNode;
  label: string;
  hint: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="flex items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition hover:bg-white/[0.04]"
    >
      <span className={cn("shrink-0", checked ? "text-[#ff8fac]" : "text-[#5f6b78]")}>
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13.5px] font-semibold text-[#e7e9ea]">{label}</span>
        <span className="block text-[11.5px] text-[#71767b]">{hint}</span>
      </span>
      <span
        className={cn(
          "relative h-5 w-9 shrink-0 rounded-full transition-colors",
          checked ? "bg-[#ff477e]" : "bg-white/[0.12]"
        )}
      >
        <span
          className={cn(
            "absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform",
            checked ? "translate-x-[18px]" : "translate-x-0.5"
          )}
        />
      </span>
    </button>
  );
}
